import { AssistanceType, CarrierId, CoverageType } from '@graphql/generated';
import { InsurancePageId } from '@lib/helpers/texts';
import reclameAqui, { ReclameAquiData } from '../reclameAqui';

export interface CarrierInsuranceProduct {
  plans: { coverages: CoverageType[]; name: string }[];
  assistances: AssistanceType[];
  pageId: InsurancePageId;
}

const comprehensiveToCoverages = [
  CoverageType.Colision,
  CoverageType.Theft,
  CoverageType.Fire,
  CoverageType.Nature,
  CoverageType.TotalLoss,
];

const thirdPartysCoverages = [
  CoverageType.ThirdPartyMaterial,
  CoverageType.ThirdPartyBody,
  CoverageType.MoralDamages,
];

const appCoverages = [CoverageType.AppDeath, CoverageType.AppInvalid];

export interface CarrierSeoContent {
  id: CarrierId;
  products: CarrierInsuranceProduct[];
  reclameAqui: ReclameAquiData;
  contacts: { name: string; phone: string }[];
  pros: string[];
  cons: string[];
  about: string;
  name: string;
  since: number;
  shortName: string;
}

const carriersContent: Record<CarrierId, CarrierSeoContent> = {
  suhai: {
    name: 'Suhai Seguradora',
    shortName: 'Suhai',
    since: 1995,
    about:
      'O propósito da Suhai Seguradora é proporcionar aos motoristas acesso ao seguro com preço justo, ampliando sua liberdade e respeitando suas escolhas.',
    id: CarrierId.Suhai,
    pros: [
      'Seguro Barato',
      'Ampla aceitação de veículos',
      'Contratação Rápida',
      'Coberturas em outros países',
    ],
    cons: [
      'Não cobre perdas parciais',
      'Guincho limitado a 200km',
      'Pagamento apenas por boleto',
    ],
    contacts: [
      {
        name: 'Aviso de roubo, furto e PT colisão',
        phone: '3003-0335',
      },
      {
        name: 'Troca de pneu, guincho e auxílio mecânico',
        phone: '0800-327-8424',
      },
      {
        name: 'Para boleto, apólice e assuntos administrativos',
        phone: '3003-0335',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
        ],
        plans: [
          {
            name: 'Suhai Auto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Suhai Auto PT',
            coverages: [CoverageType.Theft, CoverageType.TotalLoss],
          },
          {
            name: 'Suhai Auto Roubo e Terceiros',
            coverages: [
              CoverageType.Theft,
              CoverageType.ThirdPartyMaterial,
              CoverageType.ThirdPartyBody,
              CoverageType.MoralDamages,
            ],
          },
          {
            name: 'Suhai Auto Roubo, PT e Terceiros',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              ...thirdPartysCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
        ],
        plans: [
          {
            name: 'Suhai Moto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Suhai Moto PT',
            coverages: [CoverageType.Theft, CoverageType.TotalLoss],
          },
          {
            name: 'Suhai Moto Roubo e Terceiros',
            coverages: [
              CoverageType.Theft,
              CoverageType.ThirdPartyMaterial,
              CoverageType.ThirdPartyBody,
              CoverageType.MoralDamages,
            ],
          },
          {
            name: 'Suhai Moto Roubo, PT e Terceiros',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              CoverageType.ThirdPartyMaterial,
              CoverageType.ThirdPartyBody,
              CoverageType.MoralDamages,
            ],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
    reclameAqui: reclameAqui.suhai,
  },
  porto: {
    about: reclameAqui.porto.description,
    reclameAqui: reclameAqui.porto,
    name: 'Porto Seguro',
    shortName: 'Porto',
    since: 1945,
    id: CarrierId.Porto,
    pros: [
      'Excelente assistência',
      'Desconto para correntistas do Itaú',
      'Maior seguradora do país',
      '5% de desconto para pagar no cartão porto',
    ],
    cons: [
      'Aceitação menor',
      'Pagamento em 12x sem juros apenas no cartão porto',
    ],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 727 2766',
      },
      {
        name: 'Assistências 24 horas',
        phone: '333 76786',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Porto Seguro Auto Roubo e Incêndio',
            coverages: [CoverageType.Theft, CoverageType.Fire],
          },
          {
            name: 'Porto Seguro Auto Terceiros',
            coverages: thirdPartysCoverages,
          },
          {
            name: 'Porto Seguro Auto Completo',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Porto Seguro Moto Roubo e Incêndio',
            coverages: [CoverageType.Theft, CoverageType.Fire],
          },
          {
            name: 'Porto Seguro Moto Terceiros',
            coverages: thirdPartysCoverages,
          },
          {
            name: 'Porto Seguro Moto Completo',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
  },
  allianz: {
    about: reclameAqui.allianz.description,
    reclameAqui: reclameAqui.allianz,
    name: 'Allianz Seguros',
    shortName: 'Allianz',
    since: 1890,
    id: CarrierId.Allianz,
    pros: [
      'Excelente assistência',
      'Preços competitivos',
      'Pagamento em 10x sem juros',
    ],
    cons: ['Aceitação menor'],
    contacts: [
      {
        name: 'SAC',
        phone: '08000 115 215',
      },
      {
        name: 'Assistências 24 horas',
        phone: '08000 130 700',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Allianz Auto Essencial',
            coverages: thirdPartysCoverages,
          },
          {
            name: 'Allianz Auto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Allianz Moto Essencial',
            coverages: thirdPartysCoverages,
          },
          {
            name: 'Allianz Moto',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
          {
            name: 'Allianz Moto Completo',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
  },
  azul: {
    about: reclameAqui.azul.description,
    reclameAqui: reclameAqui.azul,
    name: 'Azul Seguros',
    shortName: 'Azul',
    since: 2004,
    id: CarrierId.Azul,
    pros: [
      'Desconto para correntistas do Itaú',
      'Seguro mais barato',
      '5% de desconto para pagar no cartão porto',
    ],
    cons: ['Pagamento em 12x sem juros apenas no cartão porto'],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 703 1280',
      },
      {
        name: 'Assistências 24 horas e Sinistros',
        phone: '4004 3700',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Azul Auto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Azul Auto Leve',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
          {
            name: 'Azul Auto ',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
    ],
  },
  mapfre: {
    about: reclameAqui.mapfre.description,
    reclameAqui: reclameAqui.mapfre,
    name: 'Mapfre Seguros',
    shortName: 'Mapfre',
    since: 2004,
    id: CarrierId.Mapfre,
    pros: ['Aceitação ampliada'],
    cons: ['Assistência pode demorar'],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 775 4545',
      },
      {
        name: 'Assistências 24 horas e Sinistros',
        phone: '4004 0101',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Mapfre Auto Básico',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
          {
            name: 'Mapfre Auto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Mapfre Moto Básico',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
          {
            name: 'Mapfre Moto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
  },
  bradesco: {
    about: reclameAqui.bradesco.description,
    reclameAqui: reclameAqui.bradesco,
    name: 'Bradesco Seguros',
    shortName: 'Bradesco',
    since: 1983,
    id: CarrierId.Bradesco,
    pros: [
      'Desconto para correntistas do banco',
      'Boa Assistência 24 horas',
      'Desconto na compra de outros seguros',
    ],
    cons: ['Valor um pouco acima para quem não é correntista'],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 727 9966',
      },
      {
        name: 'Assistências 24 horas e Sinistros',
        phone: '0800 701 2757',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Bradesco Auto Tradicional',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
          {
            name: 'Bradesco Auto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Bradesco Moto Tradicional',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
          {
            name: 'Bradesco Moto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
  },
  tokio: {
    about: reclameAqui.tokio.description,
    reclameAqui: reclameAqui.tokio,
    name: 'Tokio Marine',
    shortName: 'Tokio',
    since: 1959,
    id: CarrierId.Tokio,
    pros: ['Parcelar em 12x sem juros', 'Cobertura em outros países'],
    cons: ['A vistoria online demora 2 dias úteis'],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 449 0000',
      },
      {
        name: 'Assistências 24 horas e Sinistros',
        phone: ' 0300 33 86546',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Tokio Auto Classic',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
          {
            name: 'Tokio Auto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'Tokio Moto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
          {
            name: 'Tokio Moto Sugestão',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
  },
  hdi: {
    about: reclameAqui.hdi.description,
    reclameAqui: reclameAqui.hdi,
    name: 'HDI Seguros',
    shortName: 'HDI',
    since: 1996,
    id: CarrierId.Hdi,
    pros: ['Aceitação ampliada'],
    cons: ['Assistência pode demorar'],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 722 71 49',
      },
      {
        name: 'Assistências 24 horas e Sinistros',
        phone: '0800 434 4340',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'HDI Auto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
          AssistanceType.Vidros,
        ],
        plans: [
          {
            name: 'HDI Moto',
            coverages: [
              ...comprehensiveToCoverages,
              ...thirdPartysCoverages,
              ...appCoverages,
            ],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
  },
  ituran: {
    about: reclameAqui.ituran.description,
    reclameAqui: reclameAqui.ituran,
    name: 'Ituran com Seguro',
    shortName: 'Ituran',
    since: 1999,
    id: CarrierId.Ituran,
    pros: ['Mais barato', 'Rastreador incluso', 'Pagamento mensal'],
    cons: ['Obrigatório rastreador'],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 750 0012',
      },
      {
        name: 'Assistências 24 horas e Sinistros',
        phone: '0800 015 3600',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
        ],
        plans: [
          {
            name: 'Ituran Auto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Ituran Auto PT',
            coverages: [CoverageType.Theft, CoverageType.TotalLoss],
          },
          {
            name: 'Ituran Auto Combo',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              ...thirdPartysCoverages,
            ],
          },
          {
            name: 'Ituran Auto Completo',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
        ],
        pageId: 'seguro-carro',
      },
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
        ],
        plans: [
          {
            name: 'Ituran Moto Roubo',
            coverages: [CoverageType.Theft],
          },
        ],
        pageId: 'seguro-moto',
      },
    ],
  },
  youse: {
    about:
      'Youse é uma plataforma de seguros 100% digital., que você monta do seu jeito e paga mês a mês, tipo assinatura.',
    reclameAqui: reclameAqui.youse,
    name: 'Youse Seguros',
    shortName: 'Youse',
    since: 2016,
    id: CarrierId.Youse,
    pros: [
      'Mais barato',
      'Pagamento mensal',
      'Tudo pelo App',
      'Programa de indicação',
      'Youse Friends',
    ],
    cons: [],
    contacts: [
      {
        name: 'SAC',
        phone: '0800 730 9903',
      },
      {
        name: 'Ouvidoria',
        phone: '0800 730 9991',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
        ],
        plans: [
          {
            name: 'Youse Auto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Youse Auto PT',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              ...thirdPartysCoverages,
            ],
          },
          {
            name: 'Youse Auto Completo',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
        ],
        pageId: 'seguro-carro',
      },
    ],
  },
  justos: {
    about: 'Um seguro auto mais barato para quem dirige bem',
    reclameAqui: {
      address: {},
      cnpj: '40317530000182',
      description:
        'Somos uma Insurtech 100% digital: desde a contratação até a vistoria do seu veículo. Nossa missão é melhorar a sua direção e fazer do mundo um lugar mais justo e seguro ✨',
      fantasyName: 'Justos',
      name: 'Justos',
      panel: {
        default: true,
        avg: '--',
        index: {
          averageAnswerTime: '83416.0',
          finalScore: '--',
          totalNotAnswered: '0',
          totalComplains: '5',
          consumerScore: '--',
          created: '2022-09-27T16:21:13',
          companyName: '',
          start: '2022-03-01T00:00:00',
          solvedPercentual: '--',
          type: 'SIX_MONTHS',
          totalAnswered: '5',
          totalComplains30: '4',
          averageAnswerTime3M: '84037.5',
          hasStamp: 'false',
          dealAgainPercentual: '--',
          totalEvaluated: '5',
          answeredPercentual: '100.0',
          company: 'K8UsSJg-tOyG7SHb',
          end: '2022-08-31T23:59:59',
          id: '9852aa97-a819-4c78-a9ba-12f96065fcde',
          status: 'NO_INDEX',
        },
        type: 'SIX_MONTHS',
        message: '',
      },
      performanceData: {
        solvedPercentual: '--',
        answeredPercentual: '100.0',
        totalComplains: '5',
        dealAgainPercentual: '--',
        consumerScore: '--',
        averageAnswerTime: '83416.0',
        problems: [],
        status: 'NO_INDEX',
      },
      shortName: 'Justos',
      timeAgo: 'Há 1 ano',
    },
    name: 'Justos',
    shortName: 'Justos',
    since: 2016,
    id: CarrierId.Justos,
    pros: ['Mais barato', 'Pagamento mensal', 'Tudo pelo App'],
    cons: [],
    contacts: [
      {
        name: 'SAC',
        phone: '3003-0926',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
        ],
        plans: [
          {
            name: 'Justos Auto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Justos Auto PT',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              ...thirdPartysCoverages,
            ],
          },
          {
            name: 'Justos Auto Completo',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
        ],
        pageId: 'seguro-carro',
      },
    ],
  },
  zurich: {
    about:
      'Fundada na Suíça em 2013, a Zurich é uma das maiores companhias de seguro do país atualmente. Oferece seguros diversos entre individuais e empresariais.',
    reclameAqui: {
      address: {},
      cnpj: '17197385007800',
      description:
        'Fundada na Suíça em 2013, a Zurich é uma das maiores companhias de seguro do país atualmente. Oferece seguros diversos entre individuais e empresariais.',
      fantasyName: 'Zurich',
      name: 'Zurich',
      panel: {
        default: true,
        avg: '6.9',
        index: {
          averageAnswerTime: '628511.709994223',
          finalScore: '6.9',
          totalNotAnswered: '15',
          totalComplains: '1747',
          consumerScore: '5.59',
          created: '2022-12-18T18:15:37',
          companyName: '',
          start: '2022-06-01T00:00:00',
          solvedPercentual: '74.5',
          type: 'SIX_MONTHS',
          totalAnswered: '1731',
          totalComplains30: '306',
          averageAnswerTime3M: '595463.6954545454',
          hasStamp: 'false',
          dealAgainPercentual: '50.8',
          totalEvaluated: '411',
          answeredPercentual: '99.1',
          company: '1707',
          end: '2022-11-30T23:59:59',
          id: '5aa2291b-5e45-43f4-bcdc-db3c78791c92',
          status: 'REGULAR',
        },
        type: 'SIX_MONTHS',
        message: '',
      },
      performanceData: {
        solvedPercentual: '74.5',
        answeredPercentual: '99.1',
        totalComplains: '1747',
        dealAgainPercentual: '50.8',
        consumerScore: '5.59',
        averageAnswerTime: '628511.709994223',
        problems: [],
        status: 'REGULAR',
      },
      shortName: 'Justos',
      timeAgo: 'Há 21 anos',
    },
    name: 'Zurich',
    shortName: 'Zurich',
    since: 2016,
    id: CarrierId.Zurich,
    pros: [],
    cons: [],
    contacts: [
      {
        name: 'SAC',
        phone: '3003-0926',
      },
    ],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
        ],
        plans: [
          {
            name: 'Zurich Auto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Zurich Auto PT',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              ...thirdPartysCoverages,
            ],
          },
          {
            name: 'Zurich Auto Completo',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
        ],
        pageId: 'seguro-carro',
      },
    ],
  },

  sompo: {
    about:
      'Sompo Seguro Auto faz parte de um dos maiores grupos seguradores do mundo. O grupo Sompo Holdings tem sua origem no Japão e atua há mais de 130 anos no mercado de seguros.',
    reclameAqui: {
      address: {},
      cnpj: '61383493000180',
      description:
        'Sompo Seguro Auto faz parte de um dos maiores grupos seguradores do mundo. O grupo Sompo Holdings tem sua origem no Japão e atua há mais de 130 anos no mercado de seguros.',
      fantasyName: 'Sompo',
      name: 'Sompo',
      panel: {
        default: true,
        avg: '8.1',
        index: {
          averageAnswerTime: '931551.0398230088',
          finalScore: '8.1',
          totalNotAnswered: '4',
          totalComplains: '457',
          consumerScore: '7.46',
          created: '2022-12-18T20:07:15',
          companyName: '',
          start: '2022-06-01T00:00:00',
          solvedPercentual: '82.7',
          type: 'SIX_MONTHS',
          totalAnswered: '452',
          totalComplains30: '103',
          averageAnswerTime3M: '884291.5854700855',
          hasStamp: 'false',
          dealAgainPercentual: '70.9',
          totalEvaluated: '200',
          answeredPercentual: '98.9',
          company: '4706',
          end: '2022-11-30T23:59:59',
          id: '09c8c42d-3dab-4038-979d-86b5d4874fd1',
          status: 'GREAT',
        },
        type: 'SIX_MONTHS',
        message: '',
      },
      performanceData: {
        solvedPercentual: '82.7',
        answeredPercentual: '98.9',
        totalComplains: '457',
        dealAgainPercentual: '70.9',
        consumerScore: '7.46',
        averageAnswerTime: '931551.0398230088',
        problems: [],
        status: 'GREAT',
      },
      shortName: 'Sompo',
      timeAgo: 'Há 18 anos',
    },
    name: 'Sompo Seguros',
    shortName: 'Sompo',

    since: 2016,
    id: CarrierId.Sompo,
    pros: [],
    cons: [],
    contacts: [],
    products: [
      {
        assistances: [
          AssistanceType.Guincho,
          AssistanceType.Borracheiro,
          AssistanceType.Chaveiro,
          AssistanceType.Taxi,
          AssistanceType.CarroReserva,
        ],
        plans: [
          {
            name: 'Sompo Auto Roubo',
            coverages: [CoverageType.Theft],
          },
          {
            name: 'Sompo Auto PT',
            coverages: [
              CoverageType.Theft,
              CoverageType.TotalLoss,
              ...thirdPartysCoverages,
            ],
          },
          {
            name: 'Sompo Auto Completo',
            coverages: [...comprehensiveToCoverages, ...thirdPartysCoverages],
          },
        ],
        pageId: 'seguro-carro',
      },
    ],
  },
};

export default carriersContent;
